/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ListTree, Plus, Trash2, Save, Sparkles
} from 'lucide-react';
import { serviceRequestTypeService } from '../../services/serviceRequestTypeService';
import toastService from '../../utils/toastService';

const PRESETS = [
  {
    name: 'Category > Asset > Manufacturer',
    levels: [
      { name: 'Category', key: 'category' },
      { name: 'Asset', key: 'asset' },
      { name: 'Manufacturer', key: 'make' },
    ],
  },
  {
    name: 'Building > Floor > Room > Equipment',
    levels: [
      { name: 'Building', key: 'building' },
      { name: 'Floor', key: 'floor' },
      { name: 'Room', key: 'room' },
      { name: 'Equipment', key: 'equipment' },
    ],
  },
  {
    name: 'Category > Subcategory',
    levels: [
      { name: 'Category', key: 'category' },
      { name: 'Subcategory', key: 'subcategory' },
    ],
  },
];

const HierarchyLevelManager = ({ typeUid, initialLevels = [], hasActiveOptions = true, onLevelsSaved }) => {
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (initialLevels && initialLevels.length > 0) {
      setLevels(initialLevels.map((l) => ({ name: l.name, key: l.key })));
    } else {
      // Default initial preset if no levels exist
      setLevels([
        { name: 'Category', key: 'category' },
        { name: 'Asset', key: 'asset' },
        { name: 'Manufacturer', key: 'make' },
      ]);
      setIsEditing(true);
    }
  }, [initialLevels]);

  const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const handleAddLevel = () => {
    const nextIndex = levels.length + 1;
    setLevels((prev) => [
      ...prev,
      { name: `Level ${nextIndex}`, key: `level_${nextIndex}` },
    ]);
  };

  // The last remaining level can only be removed once no active options exist
  const canRemoveLastLevel = !hasActiveOptions;

  const handleRemoveLevel = (index) => {
    if (levels.length <= 1 && !canRemoveLastLevel) {
      toastService.error('Hierarchy must have at least one level while active options exist. Deactivate them first.');
      return;
    }
    setLevels((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLevelChange = (index, field, value) => {
    setLevels((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      if (field === 'name' && (!copy[index].key || copy[index].key.startsWith('level_'))) {
        copy[index].key = slugify(value);
      }
      return copy;
    });
  };

  const applyPreset = (preset) => {
    setLevels(preset.levels.map((l) => ({ ...l })));
    setIsEditing(true);
  };

  const handleSave = async () => {
    // Validate
    for (let i = 0; i < levels.length; i++) {
      const lvl = levels[i];
      if (!lvl.name.trim()) {
        toastService.error(`Level ${i + 1} display name is required.`);
        return;
      }
      if (!lvl.key.trim()) {
        toastService.error(`Level ${i + 1} programmatic key is required.`);
        return;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(lvl.key.trim())) {
        toastService.error(`Level ${i + 1} key must only contain letters, numbers, hyphens or underscores.`);
        return;
      }
    }

    const uniqueKeys = new Set(levels.map((l) => l.key.trim().toLowerCase()));
    if (uniqueKeys.size !== levels.length) {
      toastService.error('All level keys must be unique.');
      return;
    }

    const uniqueNames = new Set(levels.map((l) => l.name.trim().toLowerCase()));
    if (uniqueNames.size !== levels.length) {
      toastService.error('All level display names must be unique.');
      return;
    }

    try {
      setSaving(true);
      const res = await serviceRequestTypeService.setHierarchyLevels(typeUid, levels);
      toastService.success(
        levels.length === 0
          ? 'Hierarchy levels removed successfully'
          : 'Hierarchy schema levels saved successfully',
      );
      setIsEditing(false);
      if (onLevelsSaved) {
        onLevelsSaved(res.data?.levels || []);
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to save hierarchy levels'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-base-content/10">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-primary/10 text-primary">
            <ListTree size={20} />
          </div>
          <div>
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Tree Hierarchy Schema (N-Levels)
            </h2>
            <p className="text-xs text-base-content/50">
              Define the levels of hierarchy for this category (e.g. Category &gt; Asset &gt; Manufacturer).
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 rounded-xl border border-base-content/20 text-xs font-bold hover:bg-base-200 transition-colors"
            >
              Modify Levels
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-3 py-1.5 rounded-xl border border-base-content/20 text-xs font-bold hover:bg-base-200 transition-colors"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      {/* Preset Recommendations */}
      {isEditing && (
        <div className="p-3.5 rounded-xl bg-base-200/50 border border-base-content/5 space-y-2">
          <div className="flex items-center gap-2 text-xs font-bold text-base-content/70">
            <Sparkles size={14} className="text-primary" />
            <span>Quick Presets:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => applyPreset(p)}
                className="px-3 py-1 rounded-lg bg-base-100 hover:bg-primary hover:text-primary-content text-[11px] font-semibold border border-base-content/10 transition-all"
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb Visual Preview */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-base-100 border border-base-content/10">
        <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 mr-1">
          Active Structure:
        </span>
        {levels.length === 0 && (
          <span className="text-xs font-medium text-base-content/50">
            No levels. Saving will remove the hierarchy from this type.
          </span>
        )}
        {levels.map((lvl, index) => (
          <React.Fragment key={index}>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs font-bold shadow-sm">
              <span className="w-4 h-4 rounded-full bg-primary text-primary-content text-[10px] font-black flex items-center justify-center">
                {index + 1}
              </span>
              <span>{lvl.name || `Level ${index + 1}`}</span>
              <span className="text-[10px] font-mono opacity-60">({lvl.key || 'key'})</span>
            </div>
            {index < levels.length - 1 && (
              <span className="text-base-content/30 text-xs font-bold">→</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Editing Form */}
      {isEditing && (
        <div className="space-y-3 pt-2">
          <div className="space-y-2.5">
            {levels.map((lvl, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-3 rounded-xl bg-base-200/40 border border-base-content/10"
              >
                <div className="flex items-center gap-2 min-w-[70px]">
                  <span className="w-6 h-6 rounded-lg bg-base-300 text-base-content text-xs font-black flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className="text-xs font-bold text-base-content/60">Level</span>
                </div>

                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-base-content/50 block mb-1">
                      Display Label (e.g. Manufacturer)
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-1.5 bg-base-100 border border-base-300 rounded-lg text-xs font-medium focus:border-primary focus:outline-none"
                      placeholder="e.g. Manufacturer"
                      value={lvl.name}
                      onChange={(e) => handleLevelChange(index, 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-base-content/50 block mb-1">
                      Programmatic Key (e.g. make)
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-1.5 bg-base-100 border border-base-300 rounded-lg text-xs font-mono focus:border-primary focus:outline-none"
                      placeholder="e.g. make"
                      value={lvl.key}
                      onChange={(e) => handleLevelChange(index, 'key', e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 pt-1 sm:pt-0">
                  <button
                    type="button"
                    title={
                      levels.length <= 1 && !canRemoveLastLevel
                        ? 'Deactivate all options before removing the last level'
                        : 'Remove level'
                    }
                    disabled={levels.length <= 1 && !canRemoveLastLevel}
                    onClick={() => handleRemoveLevel(index)}
                    className="p-1.5 text-base-content/60 hover:text-error disabled:opacity-30 rounded-lg hover:bg-base-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
            <button
              type="button"
              onClick={handleAddLevel}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-dashed border-base-content/30 text-base-content/70 hover:border-primary hover:text-primary rounded-xl text-xs font-bold transition-all"
            >
              <Plus size={14} />
              <span>Add Another Level</span>
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="w-full sm:w-auto btn-premium px-5 py-2 text-xs font-bold"
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                  <span>Saving Levels...</span>
                </>
              ) : (
                <>
                  <Save size={14} />
                  <span>Save Schema Structure</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

HierarchyLevelManager.propTypes = {
  typeUid: PropTypes.string.isRequired,
  initialLevels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      key: PropTypes.string,
      depth: PropTypes.number,
    }),
  ),
  hasActiveOptions: PropTypes.bool,
  onLevelsSaved: PropTypes.func,
};

export default HierarchyLevelManager;
